import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const GF_SCROLL_GAP = 16;

/**
 * gf: come l'intl, dopo la scelta della taglia la pagina scorre alla sua foto
 * (computer: la foto nella griglia; telefono: la galleria, che parte gia' dalla foto giusta).
 * @param {Element} gallery - The media gallery just rendered.
 */
function scrollToVariantMedia(gallery) {
  if (!(gallery instanceof HTMLElement)) return;
  const index = parseInt(gallery.dataset.gfVariantIndex ?? '', 10);
  if (Number.isNaN(index)) return;

  const isDesktop = window.matchMedia('(min-width: 750px)').matches;
  const gridItem = gallery.querySelectorAll('.media-gallery__grid > li')[index];
  const target = isDesktop && gridItem ? gridItem : gallery;
  const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;

  // Sul computer il tema scorre dentro .page-wrapper (non la finestra): scrollIntoView funziona in tutti e due i casi.
  target.style.scrollMarginTop = `${headerHeight + GF_SCROLL_GAP}px`;
  target.scrollIntoView({ block: 'start', behavior: 'smooth' });
  if (!isDesktop) requestAnimationFrame(() => setTimeout(() => showSlide(gallery, index), 0));
}

/**
 * Porta il carosello del telefono sulla foto indicata (se lo slideshow non ci e' gia' arrivato da solo).
 * @param {HTMLElement} gallery
 * @param {number} index
 */
function showSlide(gallery, index) {
  const scroller = gallery.querySelector('slideshow-component [ref="scroller"]');
  const slide = gallery.querySelectorAll('slideshow-component slideshow-slide')[index];
  if (!(scroller instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

  const left = slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
  if (Math.abs(scroller.scrollLeft - left) > 2) scroller.scrollTo({ left, behavior: 'instant' });
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        scrollToVariantMedia(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
